import React, { useState, useEffect, useRef } from 'react';
import { Header } from '../../_components/shared/header';
import Loading from '../../_components/shared/loading';
import { MainService } from '../../_services';
import { ROLES } from '../../_hooks';

const EDITABLE_ROLES = [
    { key: ROLES.RDG,        label: 'RDG' },
    { key: ROLES.RCG_PRESTA, label: 'RCG Presta' },
    { key: ROLES.AGENT_GRDF, label: 'Agent GRDF' },
];

function hasRolesChanged(original, current) {
    if (original.length !== current.length) return true;
    const sort = (arr) => [...arr].sort().join(',');
    return sort(original) !== sort(current);
}

function ManageUsers() {
    const service = useRef(new MainService()).current;

    const [users,         setUsers]         = useState([]);
    const [loading,       setLoading]        = useState(true);
    const [localRoles,    setLocalRoles]     = useState({});  // { [userId]: string[] }
    const [originalRoles, setOriginalRoles]  = useState({});  // { [userId]: string[] } — référence serveur
    const [savingId,      setSavingId]       = useState(null);
    const [savedIds,      setSavedIds]       = useState(new Set());
    const [errorIds,      setErrorIds]       = useState(new Set());

    useEffect(() => {
        service.getUsers()
            .then((res) => {
                const data = res.data;
                setUsers(data);
                const initial = {};
                data.forEach((u) => { initial[u.id] = u.roles ?? []; });
                setLocalRoles(initial);
                setOriginalRoles(initial);
            })
            .finally(() => setLoading(false));
    }, []);

    const toggleRole = (userId, role) => {
        setLocalRoles((prev) => {
            const current = prev[userId] ?? [];
            const updated = current.includes(role)
                ? current.filter((r) => r !== role)
                : [...current, role];
            return { ...prev, [userId]: updated };
        });
        setSavedIds((prev) => { const s = new Set(prev); s.delete(userId); return s; });
        setErrorIds((prev) => { const s = new Set(prev); s.delete(userId); return s; });
    };

    const save = (userId) => {
        setSavingId(userId);
        setErrorIds((prev) => { const s = new Set(prev); s.delete(userId); return s; });
        const newRoles = localRoles[userId] ?? [];
        service.updateUserRoles(userId, newRoles)
            .then(() => {
                setOriginalRoles((prev) => ({ ...prev, [userId]: newRoles }));
                setSavedIds((prev) => new Set([...prev, userId]));
            })
            .catch(() => {
                setErrorIds((prev) => new Set([...prev, userId]));
            })
            .finally(() => setSavingId(null));
    };

    return (
        <>
            <Header />
            <div className="page">
                <h2 className="bleu manage-users-title">Gestion des utilisateurs</h2>
                {loading ? <Loading /> : (
                    <div className="manage-users-wrapper">
                        <table className="users-table">
                            <thead>
                                <tr>
                                    <th>Nom</th>
                                    <th>GAIA</th>
                                    <th>Email</th>
                                    {EDITABLE_ROLES.map((r) => (
                                        <th key={r.key} className="users-table-role-header">{r.label}</th>
                                    ))}
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((user) => {
                                    const roles    = localRoles[user.id] ?? [];
                                    const original = originalRoles[user.id] ?? [];
                                    const isDirty  = hasRolesChanged(original, roles);
                                    const isSaving = savingId === user.id;
                                    const isSaved  = savedIds.has(user.id);
                                    const hasError = errorIds.has(user.id);

                                    return (
                                        <tr key={user.id}>
                                            <td>{user.firstName} {user.lastName}</td>
                                            <td className="users-table-gaia">{user.gaia}</td>
                                            <td>{user.email}</td>
                                            {EDITABLE_ROLES.map((r) => (
                                                <td key={r.key} className="users-table-role-cell">
                                                    <input
                                                        type="checkbox"
                                                        className="role-toggle"
                                                        checked={roles.includes(r.key)}
                                                        onChange={() => toggleRole(user.id, r.key)}
                                                        disabled={isSaving}
                                                    />
                                                </td>
                                            ))}
                                            <td className="users-table-actions">
                                                {isDirty && (
                                                    <button
                                                        className="btn-save-roles"
                                                        onClick={() => save(user.id)}
                                                        disabled={isSaving}
                                                    >
                                                        {isSaving ? 'Enregistrement…' : 'Enregistrer'}
                                                    </button>
                                                )}
                                                {isSaved  && <span className="save-feedback save-feedback--success">Enregistré</span>}
                                                {hasError && <span className="save-feedback save-feedback--error">Erreur</span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </>
    );
}

export { ManageUsers };
